import { Fragment, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import IconSelect from '@/components/Form/IconSelect';

interface Provider {
  id?: string;
  name: string;
  icon: string;
}

interface ProviderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; icon: string }) => Promise<void>;
  provider?: Provider | null;
  title: string;
  availableIcons: { name: string; icon: string }[];
}

export default function ProviderModal({
  isOpen,
  onClose,
  onSubmit,
  provider,
  title,
  availableIcons
}: ProviderModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors }
  } = useForm<{ name: string; icon: string }>({
    defaultValues: {
      name: provider?.name || '',
      icon: provider?.icon || 'fa-truck-field'
    }
  });

  const selectedIcon = watch('icon');

  useEffect(() => {
    if (provider) {
      reset({ name: provider.name, icon: provider.icon });
    } else {
      reset({ name: '', icon: 'fa-truck-field' });
    }
  }, [provider, reset]);

  // Register icon field
  useEffect(() => {
    register('icon', { required: 'Icon is required' });
  }, [register]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-dark-bg-light p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900 dark:text-dark-text-primary flex justify-between items-center"
                >
                  {title}
                  <button
                    type="button"
                    className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                    onClick={onClose}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </Dialog.Title>

                <form onSubmit={handleSubmit(onSubmit)} className="mt-4">
                  <div className="space-y-4">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-gray-700 dark:text-dark-text-primary"
                      >
                        Provider Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        {...register('name', { required: 'Provider name is required' })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-dark-bg dark:border-dark-border dark:text-dark-text-primary sm:text-sm"
                      />
                      {errors.name && (
                        <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="icon"
                        className="block text-sm font-medium text-gray-700 dark:text-dark-text-primary"
                      >
                        Icon
                      </label>
                      <IconSelect
                        value={selectedIcon}
                        onChange={(icon) => setValue('icon', icon)}
                        options={availableIcons}
                      />
                      {errors.icon && (
                        <p className="mt-1 text-sm text-red-600">{errors.icon.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={onClose}
                      className="rounded-md border border-gray-300 bg-white dark:bg-dark-bg dark:border-dark-border dark:text-dark-text-primary px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:hover:bg-dark-bg-lighter focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="rounded-md border border-transparent bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                    >
                      {provider ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
} 